// Typed static client for the /Prescription/* endpoints (SD-2337 / PR 3a).
// All-static, returns typed data (never a raw AxiosResponse), rethrows errors.
// Follows the PositiveIdService precedent.
//
// URL note: axios.defaults.baseURL already ends in /api. The endpoint strings
// here are preserved verbatim from PrescriptionDetailsPage.vue's original call
// sites — including the deliberate mix of leading-slash forms (`api/Prescription`,
// `/api/Prescription/...`, `/Prescription/...`). This is a mechanical, behavior-
// preserving extraction; the exact request paths must not change.

import axios from 'axios'
import { DAWCode, Prescription, PrescriptionDispenseItem, RefillStatusCode } from '@/models/Prescription'
import { DrugSource } from '@/models/Drug/DrugProduct'
import { Questionary } from '@/models/Questionary'
import { EquivalentPrescription } from '@/models/EquivalentPrescription'
import { POSITIVE_ID_TOKEN_HEADER } from '@/services/PositiveIdService'
import { formatDateOnly } from '@/services/prescription/dateRules'

/** Latest non-void fill status — used to disable "New Refill" from an earlier fill. */
export interface LatestFillStatus {
    status: RefillStatusCode
    subStatus: number | null
    rfNumber: number
}

/** Result of {@link PrescriptionService.cancelPV2}. */
export interface CancelPV2Result {
    cancelled: boolean
}

/** Query inputs for the equivalent-prescriptions preflight before save. */
export interface EquivalentPrescriptionsQuery {
    patientId: number
    packageId: number
    drugSource: DrugSource
    storeId: number
    rxNumber: number
}

/** Re-point one prescription's linked image to a re-annotated image. */
export interface UpdateImageRequest {
    storeID: number
    rxNumber: number
    rfNumber: number
    newImageId: number
}

/** Audit payload recorded when the operator keeps a brand over a suggested generic. */
export interface DawOverrideLogRequest {
    brandPackageId: number | null
    brandDrugSource: DrugSource
    suggestedGenericPackageIds: number[]
    dawCode: DAWCode
    pharmacistInitials: string
}

/** Options for the prescription save POST. */
export interface SavePrescriptionOptions {
    dispensingItems: PrescriptionDispenseItem[]
    drugCategoryAnswers: Questionary[]
    /** Maps to the `createNewRefill` query parameter. */
    createNewRefill: boolean
    /** Positive-ID verified token; when present it is sent as the required header. */
    positiveIdToken?: string | null
}

/**
 * The save response is a prescription plus the server's drug-category answer-save
 * side-channel (both optional — present only when category answers rode along).
 */
export interface SavePrescriptionResponse extends Prescription {
    answerSaveFailedCategoryIDs?: number[]
    answerSaveWarning?: string
}

export class PrescriptionService {
    /** Load the full prescription by concatenated rx id (StoreID-RxNumber-RfNumber). */
    static async getById(rxId: string): Promise<Prescription> {
        const response = await axios.get<Prescription>(`api/Prescription/${rxId}`)
        return response.data
    }

    /** Contacts for a prescription — the contacts modal's b-table provider source. */
    static async getContacts(rxId: string): Promise<Prescription[]> {
        const response = await axios.get<Prescription[]>(`/api/Prescription/${rxId}/contacts`)
        return response.data
    }

    /** Most recent fill for the same patient/store/rx (the "last fill" panel). */
    static async getLastFill(patientId: number, storeId: number, rxNumber: number): Promise<Prescription> {
        const response = await axios.get<Prescription>(`/Prescription/last-fill/${patientId}/${storeId}/${rxNumber}`)
        return response.data
    }

    /** Latest non-void fill status for the rx; null when none applies. */
    static async getLatestFillStatus(storeId: number, rxNumber: number): Promise<LatestFillStatus | null> {
        const response = await axios.get<LatestFillStatus | null>(
            `/Prescription/latest-fill-status/${storeId}/${rxNumber}`,
        )
        return response.data ?? null
    }

    /** Toggle the urgent flag. Body is the raw boolean; server expects JSON content-type. */
    static async setUrgent(rxId: string, isUrgent: boolean): Promise<void> {
        await axios.post(`/Prescription/${rxId}/urgent`, isUrgent, {
            headers: { 'Content-Type': 'application/json' },
        })
    }

    /** Cancel the rx's PV2, returning inventory to stock. */
    static async cancelPV2(rxId: string): Promise<CancelPV2Result> {
        const response = await axios.get<CancelPV2Result>(`/Prescription/${rxId}/cancel-pv2/`)
        return response.data
    }

    /** Mark the fill transferred-out locally and notify Asembia. */
    static async returnToAsembia(storeId: number, rxNumber: number, rfNumber: number): Promise<void> {
        await axios.post(`/Prescription/${storeId}/${rxNumber}/${rfNumber}/ReturnToAsembia`)
    }

    /** Re-point every sibling script sharing an image to a re-annotated image. */
    static async updateSiblingImages(currentImageId: number, newImageId: number): Promise<void> {
        await axios.put('/Prescription/update-sibling-images', { currentImageId, newImageId })
    }

    /** Re-point a single prescription's linked image. */
    static async updateImage(request: UpdateImageRequest): Promise<void> {
        await axios.post('/Prescription/updateImage', request)
    }

    /** Equivalent-prescriptions preflight for the discontinue-equivalents prompt. */
    static async getEquivalentPrescriptions(query: EquivalentPrescriptionsQuery): Promise<EquivalentPrescription[]> {
        const response = await axios.get<EquivalentPrescription[]>('/Prescription/EquivalentPrescriptions', {
            params: query,
        })
        return response.data
    }

    /** Whether this brand was already kept here (recorded DAW override) for this fill. */
    static async dawOverrideLogExists(rxId: string, brandPackageId: number): Promise<boolean> {
        const response = await axios.get<boolean>(`/Prescription/${rxId}/daw-override-log/exists`, {
            params: { brandPackageId },
        })
        return response.data
    }

    /** Record the brand-kept DAW override audit row. */
    static async postDawOverrideLog(rxId: string, request: DawOverrideLogRequest): Promise<void> {
        await axios.post(`/Prescription/${rxId}/daw-override-log`, request)
    }

    /**
     * Save the prescription. Owns the save-time serialization quirks: fillDate and
     * writtenDate are formatted to YYYY-MM-DD, and the positive-ID token (when present)
     * is sent as the required header.
     */
    static async save(prescription: Prescription, options: SavePrescriptionOptions): Promise<SavePrescriptionResponse> {
        const body = {
            ...prescription,
            fillDate: formatDateOnly(prescription.fillDate),
            writtenDate: formatDateOnly(prescription.writtenDate),
            dispensingItems: options.dispensingItems,
            drugCategoryAnswers: options.drugCategoryAnswers,
        }
        const headers = options.positiveIdToken
            ? { [POSITIVE_ID_TOKEN_HEADER]: options.positiveIdToken }
            : undefined
        const response = await axios.post<SavePrescriptionResponse>('/Prescription/', body, {
            params: { createNewRefill: options.createNewRefill },
            ...(headers ? { headers } : {}),
        })
        return response.data
    }
}

export default PrescriptionService
